<template>
  <v-container>
    <!-- Диалог со способами оплаты -->
    <v-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar dark color="red">
          <v-btn icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Способы оплаты</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>

        <v-col v-for="(location, index) in locations" :key="index">
          <v-card>
            <v-card-title class="text-h5">{{ location.city }}</v-card-title>
            <v-card-text>
              <div v-for="(address, i) in location.addresses" :key="i">
                {{ address }}
              </div>
            </v-card-text>
            <v-card-actions v-if="location.mapUrl">
              <v-btn text :href="location.mapUrl">
                <v-icon left>mdi-map</v-icon>Карта
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-card>
    </v-dialog>

    <!-- Основной контент -->
    <v-row dense>
      <!-- Карточка баланса -->
      <v-col cols="12">
        <v-card :color="userdata?.status === '1' || userdata?.status === 1 ? 'green' : 'red'" theme="dark">
          <v-card-title class="text-h6 text-sm-h5">
            Баланс 
            <span 
              :class="{
                'negative-balance': parseFloat(userdata?.balance) < 0 && (userdata?.status === '1' || userdata?.status === 1),
                'negative-balance-inactive': parseFloat(userdata?.balance) < 0 && !(userdata?.status === '1' || userdata?.status === 1)
              }"
            >
              {{ userdata?.balance }} €
            </span>
          </v-card-title>
          <v-card-subtitle>
            {{ "+382 " + userdata?.phone }} <br />
            <div style="font-weight: bold">{{ userdata?.name }}</div>
            <v-chip 
              class="mt-2" 
              :color="userdata?.status === '1' || userdata?.status === 1 ? 'light-green-accent-1' : 'red-lighten-3'" 
              :text-color="userdata?.status === '1' || userdata?.status === 1 ? 'green-darken-4' : 'red-darken-4'" 
              size="small"
              variant="elevated"
              elevation="2"
            >
              <v-icon 
                start 
                size="small" 
                :icon="userdata?.status === '1' || userdata?.status === 1 ? 'mdi-check-circle' : 'mdi-alert-circle'" 
              ></v-icon>
              {{ userdata?.status === '1' || userdata?.status === 1 ? 'Активен' : 'Не активен' }}
            </v-chip>
          </v-card-subtitle>
          <v-card-actions>
            <v-btn 
              text 
              color="white" 
              href="https://montelcompany.me/#map"
            >
              <v-icon start>mdi-sim</v-icon>
              {{ userdata?.tariffName && userdata?.tariffName !== '0' ? `Тариф: ${userdata.tariffName}` : 'Тариф не выбран' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <!-- Карточка расходов -->
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-h6">Расходы</v-card-title>
          <v-card-subtitle>{{ getMonthName(userdata?.m) }}</v-card-subtitle>
          <v-table density="compact">
            <template v-slot:default>
              <tbody>
                <tr v-for="(item, key) in expenses" :key="key">
                  <td>{{ item.label }}</td>
                  <td>{{ userdata?.[item.field] }} €</td>
                </tr>
              </tbody>
            </template>
          </v-table>
          <v-card-actions>
            <v-row justify="center">
              <v-btn color="red" text @click="dialog = true">
                Способы оплаты
              </v-btn>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import axios from 'axios'
import { useUserStore } from '@/stores/user'
import { API_BASE_URL } from '@/services/api'

const userStore = useUserStore()
const dialog = ref(false)
const userdata = ref({})

// Данные о локациях
const locations = [
  {
    city: 'Budva',
    addresses: [
      'MixMarkt Ul. Zrtava Fasizma',
      'BŪREAU. Montenegro | HOT PROPERTY Budva bb'
    ],
    mapUrl: 'https://goo.gl/maps/3LdaKHXwfh5sMsDY9'
  },
  {
    city: 'Tivat',
    addresses: ['MixMarkt Jadranski put br.12, Markuševina, Tivat'],
    mapUrl: 'https://goo.gl/maps/UVrPrwAiQwZ2jgck9'
  },
  {
    city: 'Bar',
    addresses: [
      'MixMarkt Ul. Rista Lekića BB, Bar',
      'Autoskola Vozac Ul. Jovana Tomasevica, Bar'
    ],
    mapUrl: 'https://goo.gl/maps/4eLur6dS53qzocz9A'
  },
  {
    city: 'Herceg Novi',
    addresses: ['MixMarkt Mica Vavica br.1, Herceg Novi'],
    mapUrl: 'https://goo.gl/maps/PCMrABRBzWByayRr7'
  },
  {
    city: 'Podgorica',
    addresses: ['MixMarkt Stari aerodrom, Josipa Broza Tita, lamala A, Podgorica'],
    mapUrl: 'https://montelcompany.me/#map'
  }
]

// Структура расходов
const expenses = [
  { label: 'Абонентская плата ', field: 'amount' },
  { label: 'Звонки ', field: 'calls' },
  { label: 'SMS ', field: 'sms' },
  { label: 'Интернет ', field: 'gprs' },
  { label: 'Роуминг ', field: 'roaming' },
  { label: 'Платные сервисы ', field: 'services' }
]

// Получение названия месяца
const getMonthName = (month: string) => {
  if (!month) return ''
  const m = new Date(month)
  m.setMonth(m.getMonth())
  const sm = m.toLocaleString('ru-RU', { month: 'long' })
  return sm.charAt(0).toUpperCase() + sm.slice(1)
}

// Следим за состоянием аутентификации
watch(() => userStore.state.value, async (state) => {
  if (state === 'authenticated') {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/getuserinfo2?number=${
          userStore.state?.context?.user?.phoneNumber?.substring(4)
        }`
      )
      userdata.value = response.data
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }
}, { immediate: true })
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}

.v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.v-card-text {
  font-size: 1rem;
}

.negative-balance {
  color: #ff5252;
  font-weight: bold;
  background-color: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 8px;
  border: 2px solid #ff5252;
  display: inline-block;
  animation: pulse 2s infinite;
}

.negative-balance-inactive {
  color: #ffffff;
  font-weight: bold;
  background-color: rgba(0, 0, 0, 0.3);
  padding: 4px 8px;
  border-radius: 8px;
  border: 2px solid #ffffff;
  display: inline-block;
  animation: pulse-white 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 82, 82, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
  }
}

@keyframes pulse-white {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}
</style>